/* eslint-disable react/no-danger */
import PropTypes from 'prop-types';
import { Component } from 'react';
import _ from 'lodash';
import classnames from 'classnames';
import SpeedControlButton from '../SpeedControlButton';
import { utils as u } from 'edrolo-ui';
import {
  registerHashedId,
  playHashedId,
  removeWistiaEmbed,
} from '../../controller';
import debugLog from 'core/utils/debugLog';
import { withAnalytics } from 'edrolo/analytics';

const ENABLE_DEBUG_LOGGING = false;
const log = ENABLE_DEBUG_LOGGING ? debugLog : () => {};

/******************************************************************************
    _    _                 ____         _____
   | |  | |               |  _ \       |  __ \
   | |__| | ___ _ __ ___  | |_) | ___  | |  | |_ __ __ _  __ _  ___  _ __  ___
   |  __  |/ _ \ '__/ _ \ |  _ < / _ \ | |  | | '__/ _` |/ _` |/ _ \| '_ \/ __|
   | |  | |  __/ | |  __/ | |_) |  __/ | |__| | | | (_| | (_| | (_) | | | \__ \
   |_|  |_|\___|_|  \___| |____/ \___| |_____/|_|  \__,_|\__, |\___/|_| |_|___/
                                                        __/ |
                                                       |___/

   Note:
 * Wistia is a fickle beast and intuition is rarely useful
 * Be prepared to test all analytics and onPlay onEnd event handling etc
 * Small changes have non-obvious consequences
 * Godspeed
 ******************************************************************************/
@withAnalytics
class WistiaPlayer extends Component {
  componentDidMount() {
    log('componentDidMount');
    this.prepareWistiaVideo();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.videoHash !== this.props.videoHash) {
      log(
        `componentDidUpdate: moved from ${prevProps.videoHash} to ${this.props.videoHash}`
      );
      this.prepareWistiaVideo();
      playHashedId(this.embedHash, this.wistiaProperties);
    }
  }

  get containerStyle() {
    const { introWistiaHash, thumbnailUrl, height } = this.props;

    // backgrounds look weird between the preroll and video
    if (introWistiaHash) {
      return {};
    }

    const containerStyle = {
      backgroundSize: 'cover',
    };

    if (thumbnailUrl) {
      containerStyle.backgroundImage = thumbnailUrl;
    }

    if (height) {
      containerStyle.minHeight = this.props.height;
    }

    return containerStyle;
  }

  get wistiaProperties() {
    return {
      autoPlay: this.props.autoPlay,
      playerColor: this.props.playerColor,
      fullscreenButton: this.props.fullscreenButton,
      controlsVisibleOnLoad: this.props.controlsVisibleOnLoad,
      volume: this.props.playbackVolume,
      playerPreference: 'html5',
      videoFoam: true,
      playbackRateControl: false,
      preload: true,
      wmode: 'transparent',
    };
  }

  get embedHash() {
    return this.props.introWistiaHash || this.props.videoHash;
  }

  get wistiaClassProps() {
    // converts the above properties to be async classname style `key=val`
    const props = classnames(
      'wistia_embed',
      `wistia_async_${this.embedHash}`,
      ..._.map(this.wistiaProperties, (value, key) => `${key}=${value}`)
    );
    return props;
  }

  prepareWistiaVideo() {
    const { introWistiaHash, videoHash, captionsVisible, onEnd } = this.props;

    registerHashedId({
      hashedId: videoHash,
      prerollHashedId: introWistiaHash,
      captionsVisible,
      onEnd,
      onPlay: this.handleVideoPlayed,
      onVolumeChange: this.handleVolumeChange,
      onCaptionChange: this.handleCaptionChange,
      onVideoElement: this.handleRegisterElement,
    });
  }

  handleRegisterElement = (videoElement) => {
    log('videoElementRegistered', videoElement);
    this.props.addVideoElement(videoElement);
  };

  handleVideoPlayed = () => {
    const {
      analytics: { track },
      playbackRate,
      captionsVisible,
      origin,
      appLabel,
      model,
      objectId,
      playEventName,
    } = this.props;

    track('played wistia video', {
      origin,
      app: appLabel,
      model,
      object_id: objectId,
      playEventName, // extra event name to aid targeting specific views (solutions etc)
      playbackRate,
      captionsVisible,
    });
  };

  // Volume change fires rapidly as changing. This only fires on the resting place.
  handleVolumeChange = _.debounce(this.props.setPlaybackVolume, 300, {
    leading: false,
    trailing: true,
  });

  handleCaptionChange = ({ visible }) => {
    this.props.setCaptionsVisible(visible);
  };

  render() {
    const style = this.props.height ? { height: this.props.height } : {};
    log('rendering WistiaPlayer');
    return (
      <div className={`wistia-video ${u.border}`}>
        {this.props.speedControl && <SpeedControlButton />}
        <div className="clearfix" />
        <div style={this.containerStyle}>
          <div
            id={`main_player_${this.embedHash}`}
            style={style}
            className={this.wistiaClassProps}
          />
        </div>
      </div>
    );
  }

  componentWillUnmount() {
    removeWistiaEmbed(this.embedHash);
  }
}

const noop = (f) => f;

WistiaPlayer.defaultProps = {
  introWistiaHash: '', // eg, 'k5oqkvc6o2' hackathon intro
  autoPlay: false,
  fullscreenButton: true,
  controlsVisibleOnLoad: false,
  speedControl: false,
  playerColor: '448abf',
  playbackRate: 1,
  playbackVolume: 1,
  captionsVisible: false,
  onPlay: noop,
  onEnd: noop,
  setPlaybackRate: noop,
  setPlaybackVolume: noop,
  setCaptionsVisible: noop,
  addVideoElement: noop,
};

WistiaPlayer.propTypes = {
  videoHash: PropTypes.string.isRequired,
  introWistiaHash: PropTypes.string,
  thumbnailUrl: PropTypes.string,
  autoPlay: PropTypes.bool,
  fullscreenButton: PropTypes.bool,
  controlsVisibleOnLoad: PropTypes.bool,
  speedControl: PropTypes.bool,
  playerColor: PropTypes.string,
  playbackRate: PropTypes.number,
  playbackVolume: PropTypes.number,
  captionsVisible: PropTypes.bool,
  onEnd: PropTypes.func,
  setPlaybackVolume: PropTypes.func,
  setCaptionsVisible: PropTypes.func,
  addVideoElement: PropTypes.func,
  height: PropTypes.number,
  // analytics tracking props from here on out
  analytics: PropTypes.shape({
    track: PropTypes.func,
  }),
  origin: PropTypes.string,
  appLabel: PropTypes.string,
  model: PropTypes.string,
  objectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  playEventName: PropTypes.string,
};

export default WistiaPlayer;
